import { useCallback, useEffect, useRef, useState } from 'react';
import { frontDoorOn } from '@/lib/frontDoor/flag';
import { autoStarts, claimDraft, clearDraft, readDraft, type Draft } from '@/lib/frontDoor/draft';
import { releaseCover } from '@/lib/frontDoor/cover';

type Phase = 'idle' | 'filled' | 'waiting' | 'started';

const READY_WAIT_MS = 20_000;
const TAB = Math.random().toString(36).slice(2);

interface ResumeOptions {
  /** The home screen can start a video: team loaded, the credit check answered (no key dialog for a new account), Terms accepted. */
  ready: boolean;
  /** The person has to answer something first (the Terms): wait for them with no fallback to the banner. */
  paused: boolean;
  /** Puts the draft into the home screen's box and look. */
  fill: (draft: Draft) => void;
  /** The home screen's own start, called once the filled state has rendered. */
  start: () => void;
}

/**
 * Picks up what a visitor typed on the landing page. A draft armed in the last 30 minutes that this tab claims
 * starts by itself, under the storyboard view (cover.ts); any other draft waits behind a "Pick up where you left
 * off" banner.
 */
export function useFrontDoorResume({ ready, paused, fill, start }: ResumeOptions) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [offered, setOffered] = useState<Draft | null>(null);
  const fillRef = useRef(fill);
  const startRef = useRef(start);
  fillRef.current = fill;
  startRef.current = start;

  const pickUp = useCallback((draft: Draft) => {
    setOffered(null);
    fillRef.current(draft);
    setPhase('filled');
  }, []);

  const go = useCallback(() => {
    setPhase('started');
    setOffered(null);
    clearDraft();
    startRef.current();
  }, []);

  useEffect(() => {
    if (!frontDoorOn()) return;
    const draft = readDraft();
    if (draft && autoStarts(draft) && claimDraft(TAB)) return pickUp(draft);
    releaseCover();
    if (draft) setOffered(draft);
  }, [pickUp]);

  useEffect(() => {
    if (phase !== 'filled') return;
    if (ready) {
      go();
      return;
    }
    if (paused) return;
    const timer = setTimeout(() => {
      setPhase('waiting');
      releaseCover();
    }, READY_WAIT_MS);
    return () => clearTimeout(timer);
  }, [phase, ready, paused, go]);

  const onStart = useCallback(() => {
    if (phase === 'waiting') return go();
    const draft = readDraft();
    if (draft) pickUp(draft);
    else setOffered(null);
  }, [phase, go, pickUp]);

  const onDiscard = useCallback(() => {
    setOffered(null);
    setPhase('idle');
    clearDraft();
  }, []);

  const banner = phase === 'waiting' ? readDraft() : offered;
  return { banner, onStart, onDiscard };
}
